import React, { useState, useEffect, useRef } from 'react';
import { ProjectProgress, BudgetChart, IssueChart } from '../../components/charts/DashboardCharts';
import { CalenderDays } from "../../modules/default-svg";
import {
  dateOnlyFormatted,
  getAccountCurrency,
  displayDecimalFormatted,
  weeksBetweenDates
} from "../../utils/systemSettings";
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import useNavigation from '../../utils/useNavigation';
import { getNavigationFromEntityType } from './utils/helper';

const SingleOverviewTable = ({
  overviewDetails,
  dashboardFilterSetName,
  dashboardFilterSetId,
  loading,
  onComponentReady,
  entityType
}) => {
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);

  const programs = overviewDetails || [];
  const containerRef = useRef(null);
  const { navigateTo } = useNavigation();

  useEffect(() => {
    if (onComponentReady) {
      const timeout = setTimeout(() => {
        onComponentReady();
      }, 0);
      return () => clearTimeout(timeout);
    }
  }, [onComponentReady]);

  const handleRedirect = (item) => {
    let navigationType = getNavigationFromEntityType(entityType);
    if (navigationType) {
      navigateTo(navigationType, item.id, item.name);
    }
  };
  const categories = [
    {
      key: 'date',
      label: 'Date',
      render: (program) => (
        <div className="flex items-center justify-center gap-2">
          <CalenderDays />
          {!program.startDate && !program.endDate ? '' : (
            <>
              {program.startDate ? dateOnlyFormatted(setting, program.startDate) : ''} -{' '}
              {program.endDate ? dateOnlyFormatted(setting, program.endDate) : ''}
            </>
          )}
        </div>
      )
    },
    {
      key: 'remaining',
      label: 'Duration',
      render: (program) => (
        <div className="flex justify-center">
          {(program.startDate && program.endDate)
            ? weeksBetweenDates(program.startDate, program.endDate)
            : '-'}
        </div>
      )
    },
    {
      key: 'progress',
      label: 'Progress',
      render: (program) => (
        <div className="flex justify-center" style={{ zIndex: 0 }}>
          <ProjectProgress progress={program.progress} />
        </div>
      )
    },
    {
      key: 'cost',
      label: 'Budget',
      render: (program) => (
        <div className="flex justify-center">
          <BudgetChart
            budgetSummary={program.costs}
            currencySymbol={getAccountCurrency(setting)}
          />
        </div>
      )
    },
    {
      key: 'issues',
      label: 'Issues',
      render: (program) => (
        <div className="flex justify-center w-full">
          <IssueChart issues={program.issues || []} />
        </div>
      )
    }
  ];

  return (
    <div className="DashboardCard overview-table w-full p-0" ref={containerRef}>
      <div className="">
        <table className="w-full border-collapse min-w-[800px]">
          <thead>
            <tr className="border-b">
              <th className="text-left p-3 sticky left-0 w-48">{entityType}</th>
              {categories.map(category => (
                <th key={category.key} className="text-center p-3 ">{category.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              // Render 1 dummy row
              <tr>
                <td className="sticky left-0 p-3 ">
                  <Skeleton width={100} height={20} />
                </td>
                {categories.map((_, index) => (
                  <td key={index} className="p-3 text-center">
                    <Skeleton width="100%" height={30} />
                  </td>
                ))}
              </tr>
            ) : programs.length === 0 ? (
              <tr>
                <td colSpan={categories.length + 1} className="text-center p-4 text-gray-500">
                  No data available.
                </td>
              </tr>
            ) : (
              programs.map(program => (
                <tr key={program.id} >
                  <td
                    className="text-center p-3 sticky left-0 font-semibold cursor-pointer overview-header"
                    style={{ minWidth: "12rem", maxWidth: "12rem", width: "12rem" }}
                    onClick={() => {
                      handleRedirect({ id: program.id, name: program.name });
                    }}
                  >
                    <div className="d-flex flex-col gap-1 align-items-center justify-center">
                      {program.image &&
                        (
                          <div className="flex flex-col items-center justify-center min-w-10 max-w-30 h-7 h-4 overflow-hidden mr-1">
                            <img src={program.image} className="max-w-full max-h-full object-contain" />
                          </div>
                        )}
                      <p style={{ minWidth: "11rem", maxWidth: "11rem", width: "11rem", whiteSpace: "normal" }}>{program.name}</p>
                    </div>
                  </td>
                  {categories.map(category => (
                    <td key={category.key} className="p-3 text-center">
                      {category.render(program)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SingleOverviewTable;
